import Delete from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/Upload';
import { IconButton, Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';

interface DragDropFileProps {
  onFileDrop: (file: File) => void;
  file: File | '';
  supportedFileTypes: string | '';
  maxFileSize: number | 0;
  disabled: boolean | false;
  onDelete?: () => void;
  resumeUrl?: string;
  title?: string;
  /**
   * DOM id tying the label to the input.
   *
   * It was hardcoded to "file-upload", which is fine for one instance and wrong for two: ids must
   * be unique in a document, so a second drop zone on the same page rendered a label pointing at
   * the FIRST zone's input. Clicking it opened the wrong picker and the file landed in the wrong
   * control. The guide editor is the page that now has two.
   *
   * Defaulted, so every existing caller keeps working unchanged.
   */
  inputId?: string;
}

const FileUpload = ({ onFileDrop, file, supportedFileTypes, maxFileSize, disabled, onDelete, resumeUrl, title, inputId = 'file-upload', }: DragDropFileProps) => {
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const validateFiles = (files: FileList) => {
    if (files.length > 1) {
      setError('Only one file can be uploaded.');
      return false;
    }

    const file = files[0];
    if (file.name.split('.').pop() && !supportedFileTypes.includes(file.name.split('.').pop()!)) {
      setError('Unsupported file type. Please upload an image.');
      return false;
    }

    if (file.size > maxFileSize) {
      setError(`File size should be less than ${maxFileSize} MB.`);
      return false;
    }

    setError('');
    return true;
  };

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragging(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        if (validateFiles(files)) {
          onFileDrop(files[0]);
        }
      }
    },
    [onFileDrop],
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      if (validateFiles(files)) {
        onFileDrop(files[0]);
      }
    }
  };

  return (
    <div
      className={`file-upload-root ${dragging ? 'dragging' : 'default'} ${file || resumeUrl ? 'padded' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
  onDrop={handleDrop}
    >
      {!file && !resumeUrl && (
        <div className="file-upload-inner">
            <label
              className={`file-upload-label ${disabled ? 'disabled' : ''}`}
              htmlFor={inputId}
            >
              <UploadIcon className="file-upload-icon" />
              <Typography align="left" className="file-upload-title" variant="body2">
                {title}
              </Typography>
            </label>
            <input
              accept={supportedFileTypes}
              className="file-upload-hidden"
              disabled={disabled}
              id={inputId}
              type="file"
              onChange={handleFileSelect}
            />
          </div>
      )
      }
      {
        (file || resumeUrl) && (
          <div className="file-upload-info">
            {file && (
              <>
                <span>{file.name}</span>
                <span>{(file.size / 1024 / 1024).toFixed(2)} MB</span>
              </>
            )}
            {resumeUrl && (
              <span>Resume URL: {resumeUrl}</span>
            )}
            {onDelete && (
              <IconButton
                aria-label="delete"
                className="file-upload-delete"
                color="error"
                onClick={onDelete}
              >
                <Delete />
              </IconButton>
            )}
          </div>
        )
      }
      {
        error && (
          <div className="file-upload-error">
            {error}
          </div>
        )
      }
    </div >
  );
};

export default FileUpload;